import size from 'lodash/size';
import isArray from 'lodash/isArray';
import last from 'lodash/last';
import isString from 'lodash/isString';
import isObject from 'lodash/isObject';
import isEmpty from 'lodash/isEmpty';

import {
    CATEGORY_TAG_TYPE,
    FORMAT_TAG_TYPE,
    EVENTBRITE_CATEGORY_TO_REFINEMENT,
    EVENTBRITE_FORMAT_TO_REFINEMENT,
    DATES_TO_REFINEMENT,
} from '../constants';

/*
 * @param tagType -- string -- oneOf([CATEGORY_TAG_TYPE, FORMAT_TAG_TYPE])
 * @param tags array
 *
 * Returns first tag that matches passed in type
 */
export const findTagByType = (tagType, tags) =>
    tags.find((tag) => tag.indexOf(tagType) > -1);

const _parseTagsForUrl = (tags) => {
    let category;

    let format;
    // if tags is an array, take the first one that represents a category
    // if is an string, take it as is if it represents a category

    if (tags && isArray(tags) && size(tags)) {
        category = findTagByType(CATEGORY_TAG_TYPE, tags);
        format = findTagByType(FORMAT_TAG_TYPE, tags);
    } else if (tags && isString(tags)) {
        category = tags.indexOf(CATEGORY_TAG_TYPE) > -1 ? tags : '';
        format = tags.indexOf(FORMAT_TAG_TYPE) > -1 ? tags : '';
    }

    return { category, format };
};

const _getDateForUrl = (dates) => {
    let date;

    if (dates && isArray(dates) && size(dates)) {
        date = last(dates);
    } else if (dates && isString(dates)) {
        date = dates;
    }

    return date;
};

const _formatNameForUrl = (name) => {
    let parsedName = name || '';

    if (!isString(parsedName)) {
        return parsedName;
    }

    parsedName = parsedName
        .trim()
        .replace('.', '')
        .replace(',', '')
        .replace(' & ', ' and ');

    parsedName = parsedName.replace(/[ +,/]+/g, '-');

    return parsedName.toLowerCase();
};

const _formUrl = ({ slug, refinements, query, bbox }) => {
    let url = '/d/';
    const escapedQuery = encodeURIComponent(query);

    // When we have a bounding box it's because we couldn't find a valid slug.
    // Default to something generic like "united states" in that case
    url += bbox || isEmpty(slug) ? 'united-states/' : `${slug}/`;

    if (refinements === 'events') {
        url += escapedQuery ? `${escapedQuery}/` : 'all-events/';
    } else {
        url += escapedQuery
            ? `${refinements}/${escapedQuery}/`
            : `${refinements}/`;
    }

    if (bbox && isObject(bbox)) {
        url += `?bbox=${bbox.sw.lng},${bbox.sw.lat},${bbox.ne.lng},${bbox.ne.lat}`;
    }

    return url;
};

/**
 * Given an object with all the search params, it will build the canonical ulr for it:
 *
 * @param slug {string} -- will expect something like: "ca--san-francisco"
 * @param bbox {object} -- the lat/lng bounding box of the location
 * @param price {string} --
 * @param tags {array} -- will expect an array of EVENTBRITE_CATEGORY_TO_REFINEMENT or EVENTBRITE_FORMAT_TO_REFINEMENT keys
 * @param dates {string} -- will expect an array of the dates expected like: "today" or "tomorrow"
 * @param q {string -- the custom query made.
 *
 * Returns a formatted absolute path with the refinements in place.
 */
export const getCanonicalSearchUrl = ({
    slug,
    bbox,
    price,
    tags,
    dates,
    q,
}) => {
    let refinements = [];

    // price
    if (price) {
        refinements.push(price);
    }

    // category & format
    const { category, format } = _parseTagsForUrl(tags);

    if (category && EVENTBRITE_CATEGORY_TO_REFINEMENT[category]) {
        refinements.push(EVENTBRITE_CATEGORY_TO_REFINEMENT[category]);
    }

    if (format && EVENTBRITE_FORMAT_TO_REFINEMENT[format]) {
        refinements.push(EVENTBRITE_FORMAT_TO_REFINEMENT[format]);
    } else {
        refinements.push('events');
    }

    // dates
    const date = _getDateForUrl(dates);

    if (date && DATES_TO_REFINEMENT[date]) {
        refinements.push(DATES_TO_REFINEMENT[date]);
    }

    refinements = refinements.join('--');

    // query
    const query = _formatNameForUrl(q);

    return _formUrl({ slug, query, refinements, bbox });
};
